/**
 * DataTable Component
 *
 * A flexible data table with pagination, sorting, and search support.
 * Uses TanStack Table v8 for core functionality.
 */

import { useState, useMemo, Fragment } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getExpandedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type PaginationState,
  type ColumnFiltersState,
  type ExpandedState,
  type Row,
} from '@tanstack/react-table';
import { ChevronUp, ChevronDown, ChevronsUpDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ChevronRight as ChevronRightIcon, Search, X } from 'lucide-react';

import { cn } from '@/utils/cn';
import { Button } from '../Button';
import { Input } from '../Input';
import { Loader } from '../Loader';

// ============================================
// Types
// ============================================

export interface DataTableProps<TData> {
  /** Column definitions */
  columns: ColumnDef<TData, unknown>[];
  /** Table data */
  data: TData[];
  /** Loading state */
  isLoading?: boolean;
  /** Enable global search */
  searchable?: boolean;
  /** Search placeholder */
  searchPlaceholder?: string;
  /** Server-side search - search value */
  searchValue?: string;
  /** Server-side search - search change handler */
  onSearchChange?: (search: string) => void;
  /** Enable pagination */
  paginated?: boolean;
  /** Page sizes options */
  pageSizeOptions?: number[];
  /** Initial page size */
  initialPageSize?: number;
  /** Server-side pagination - total row count */
  totalRows?: number;
  /** Server-side pagination - current page index */
  pageIndex?: number;
  /** Server-side pagination - page size */
  pageSize?: number;
  /** Server-side pagination - page change handler */
  onPaginationChange?: (pagination: { pageIndex: number; pageSize: number }) => void;
  /** Server-side sorting - sort state */
  sortingState?: SortingState;
  /** Server-side sorting - sort change handler */
  onSortingChange?: (sorting: SortingState) => void;
  /** Row click handler */
  onRowClick?: (row: TData) => void;
  /** Empty state message */
  emptyMessage?: string;
  /** Empty state description */
  emptyDescription?: string;
  /** Custom empty state component */
  emptyComponent?: React.ReactNode;
  /** Additional table class */
  className?: string;
  /** Additional container class */
  containerClassName?: string;
  /** Enable row selection */
  enableRowSelection?: boolean;
  /** Row selection state */
  rowSelection?: Record<string, boolean>;
  /** Row selection change handler */
  onRowSelectionChange?: (selection: Record<string, boolean>) => void;
  /** Get row ID function */
  getRowId?: (row: TData) => string;
  /** Header actions (filters, bulk actions, etc.) */
  headerActions?: React.ReactNode;
  /** Sticky header */
  stickyHeader?: boolean;
  /**
   * Render expandable sub-content for a row. When provided, an expander
   * column is prepended and clicking it toggles the sub-row.
   */
  renderSubRow?: (row: TData) => React.ReactNode;
}

// ============================================
// DataTable Component
// ============================================

export function DataTable<TData>({
  columns,
  data,
  isLoading = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  searchValue: controlledSearchValue,
  onSearchChange,
  paginated = true,
  pageSizeOptions = [10, 20, 50, 100],
  initialPageSize = 10,
  totalRows,
  pageIndex: controlledPageIndex,
  pageSize: controlledPageSize,
  onPaginationChange,
  sortingState: controlledSorting,
  onSortingChange,
  onRowClick,
  emptyMessage = 'No data found',
  emptyDescription,
  emptyComponent,
  className,
  containerClassName,
  enableRowSelection = false,
  rowSelection: controlledRowSelection,
  onRowSelectionChange,
  getRowId,
  headerActions,
  stickyHeader = false,
  renderSubRow,
}: DataTableProps<TData>) {
  // Internal state for client-side operations
  const [internalSorting, setInternalSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const [internalPagination, setInternalPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: initialPageSize,
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [internalRowSelection, setInternalRowSelection] = useState<Record<string, boolean>>({});

  // Determine if server-side pagination/sorting
  const isServerSide = totalRows !== undefined;

  // Use controlled state if provided
  const sorting = controlledSorting ?? internalSorting;
  const pagination = isServerSide
    ? { pageIndex: controlledPageIndex ?? 0, pageSize: controlledPageSize ?? initialPageSize }
    : internalPagination;
  const rowSelection = controlledRowSelection ?? internalRowSelection;
  const searchValue = controlledSearchValue ?? globalFilter;

  // Handlers
  const handleSortingChange = (updater: SortingState | ((prev: SortingState) => SortingState)) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    if (onSortingChange) {
      onSortingChange(newSorting);
    } else {
      setInternalSorting(newSorting);
    }
  };

  const handlePaginationChange = (updater: PaginationState | ((prev: PaginationState) => PaginationState)) => {
    const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
    if (onPaginationChange) {
      onPaginationChange(newPagination);
    } else {
      setInternalPagination(newPagination);
    }
  };

  const handleRowSelectionChange = (updater: Record<string, boolean> | ((prev: Record<string, boolean>) => Record<string, boolean>)) => {
    const newSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
    if (onRowSelectionChange) {
      onRowSelectionChange(newSelection);
    } else {
      setInternalRowSelection(newSelection);
    }
  };

  const handleSearchChange = (value: string) => {
    if (onSearchChange) {
      onSearchChange(value);
    } else {
      setGlobalFilter(value);
    }
  };

  // Table instance
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
      globalFilter,
      columnFilters,
      rowSelection,
      expanded,
    },
    pageCount: isServerSide ? Math.ceil((totalRows ?? 0) / pagination.pageSize) : undefined,
    manualPagination: isServerSide,
    manualSorting: isServerSide,
    onSortingChange: handleSortingChange as never,
    onPaginationChange: handlePaginationChange as never,
    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onRowSelectionChange: handleRowSelectionChange as never,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: isServerSide ? undefined : getSortedRowModel(),
    getFilteredRowModel: isServerSide ? undefined : getFilteredRowModel(),
    getPaginationRowModel: isServerSide ? undefined : getPaginationRowModel(),
    getExpandedRowModel: renderSubRow ? getExpandedRowModel() : undefined,
    getRowCanExpand: renderSubRow ? () => true : undefined,
    enableRowSelection,
    getRowId,
  });

  // Total columns including the optional expander column (for colSpan).
  const totalColSpan = columns.length + (renderSubRow ? 1 : 0);

  // Computed values
  const pageCount = table.getPageCount();
  const currentPage = pagination.pageIndex + 1;
  const totalItems = isServerSide ? totalRows : table.getFilteredRowModel().rows.length;

  // Page info text
  const pageInfo = useMemo(() => {
    if (!totalItems) return 'No items';
    const start = pagination.pageIndex * pagination.pageSize + 1;
    const end = Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalItems);
    return `Showing ${start} to ${end} of ${totalItems} items`;
  }, [pagination.pageIndex, pagination.pageSize, totalItems]);

  return (
    <div className={cn('space-y-4 px-4 py-4', containerClassName)}>
      {/* Header: Search + Actions */}
      {(searchable || headerActions) && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {searchable && (
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-foreground-muted dark:text-foreground-dark-muted" />
              <Input
                type="text"
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchValue && (
                <button
                  type="button"
                  onClick={() => handleSearchChange('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-foreground-muted dark:text-foreground-dark-muted hover:text-foreground dark:hover:text-foreground-dark transition-colors"
                  aria-label="Clear search"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
          {headerActions && <div className="flex items-center gap-2">{headerActions}</div>}
        </div>
      )}

      {/* Table */}
      <div className={cn('overflow-auto rounded-lg border border-border dark:border-border-dark', className)}>
        <table className="w-full border-collapse">
          <thead
            className={cn(
              'bg-secondary-50 dark:bg-secondary-900/30',
              stickyHeader && 'sticky top-0 z-10'
            )}
          >
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {renderSubRow && (
                  <th
                    className="w-10 px-4 py-3 border-b border-border dark:border-border-dark"
                    aria-hidden
                  />
                )}
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sortDirection = header.column.getIsSorted();

                  return (
                    <th
                      key={header.id}
                      className={cn(
                        'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider',
                        'text-foreground-muted dark:text-foreground-dark-muted',
                        'border-b border-border dark:border-border-dark',
                        canSort && 'cursor-pointer select-none hover:bg-secondary-100 dark:hover:bg-secondary-800/30'
                      )}
                      style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                    >
                      <div className="flex items-center gap-2">
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {canSort && (
                          <span className="flex-shrink-0">
                            {sortDirection === 'asc' ? (
                              <ChevronUp className="h-4 w-4" />
                            ) : sortDirection === 'desc' ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronsUpDown className="h-4 w-4 opacity-50" />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className="bg-card dark:bg-card-dark">
            {isLoading ? (
              <tr>
                <td colSpan={totalColSpan} className="px-4 py-12 text-center">
                  <Loader size="lg" message="Loading data..." />
                </td>
              </tr>
            ) : table.getRowModel().rows.length === 0 ? (
              <tr>
                <td colSpan={totalColSpan} className="px-4 py-12 text-center">
                  {emptyComponent || (
                    <div className="flex flex-col items-center justify-center space-y-2">
                      <p className="text-lg font-medium text-foreground dark:text-foreground-dark">
                        {emptyMessage}
                      </p>
                      {emptyDescription && (
                        <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                          {emptyDescription}
                        </p>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row: Row<TData>) => (
                <Fragment key={row.id}>
                  <tr
                    className={cn(
                      'border-b border-border dark:border-border-dark last:border-0',
                      'transition-colors hover:bg-secondary-50 dark:hover:bg-secondary-900/20',
                      onRowClick && 'cursor-pointer',
                      row.getIsExpanded() && 'bg-secondary-50 dark:bg-secondary-900/20',
                      row.getIsSelected() && 'bg-primary-50 dark:bg-primary-900/20'
                    )}
                    onClick={() => onRowClick?.(row.original)}
                  >
                    {renderSubRow && (
                      <td className="w-10 px-4 py-3 align-middle">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            // Accordion: only one sub-row open at a time.
                            setExpanded((prev) => {
                              const isOpen = prev === true ? true : Boolean(prev[row.id]);
                              return isOpen ? {} : { [row.id]: true };
                            });
                          }}
                          aria-label={row.getIsExpanded() ? 'Collapse row' : 'Expand row'}
                          className="flex h-6 w-6 items-center justify-center rounded text-foreground-muted transition-colors hover:bg-secondary-100 hover:text-foreground dark:text-foreground-dark-muted dark:hover:bg-secondary-800 dark:hover:text-foreground-dark"
                        >
                          <ChevronRightIcon
                            className={cn(
                              'h-4 w-4 transition-transform',
                              row.getIsExpanded() && 'rotate-90'
                            )}
                          />
                        </button>
                      </td>
                    )}
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-4 py-3 text-sm text-foreground dark:text-foreground-dark"
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                  {renderSubRow && row.getIsExpanded() && (
                    <tr className="bg-secondary-50/50 dark:bg-secondary-900/10">
                      <td
                        colSpan={totalColSpan}
                        className="px-4 py-4 border-b border-border dark:border-border-dark"
                      >
                        {renderSubRow(row.original)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {paginated && !isLoading && (totalItems ?? 0) > 0 && (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {/* Page info */}
          <p className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
            {pageInfo}
          </p>

          <div className="flex items-center gap-4">
            {/* Page size selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-foreground-muted dark:text-foreground-dark-muted">
                Rows per page:
              </span>
              <select
                value={pagination.pageSize}
                onChange={(e) => {
                  const newSize = Number(e.target.value);
                  handlePaginationChange({ pageIndex: 0, pageSize: newSize });
                }}
                className={cn(
                  'h-9 rounded-md border border-border dark:border-border-dark',
                  'bg-card dark:bg-card-dark px-2 py-1 text-sm',
                  'text-foreground dark:text-foreground-dark',
                  'focus:outline-none focus:ring-2 focus:ring-primary'
                )}
              >
                {pageSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </div>

            {/* Page navigation */}
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="icon"
                onClick={() => handlePaginationChange({ ...pagination, pageIndex: 0 })}
                disabled={pagination.pageIndex === 0}
                aria-label="First page"
              >
                <ChevronsLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => handlePaginationChange({ ...pagination, pageIndex: pagination.pageIndex - 1 })}
                disabled={pagination.pageIndex === 0}
                aria-label="Previous page"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>

              <span className="mx-2 text-sm text-foreground dark:text-foreground-dark">
                Page {currentPage} of {pageCount || 1}
              </span>

              <Button
                variant="outline"
                size="icon"
                onClick={() => handlePaginationChange({ ...pagination, pageIndex: pagination.pageIndex + 1 })}
                disabled={pagination.pageIndex >= pageCount - 1}
                aria-label="Next page"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => handlePaginationChange({ ...pagination, pageIndex: pageCount - 1 })}
                disabled={pagination.pageIndex >= pageCount - 1}
                aria-label="Last page"
              >
                <ChevronsRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
